import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  useWorkshopAttention,
  useWorkshopCycleTime,
  useWorkshopEventVolume,
  useWorkshopGroupsCreated,
  useWorkshopTat,
  useWorkshopVolume,
} from "../../lib/api";
import { useAnalyticsScope, ALL } from "../../lib/useAnalyticsScope";
import { dailyAverage } from "../../lib/msdAnalytics";
import { MsdPage, useMsdToast } from "../../components/msd/Shell";
import { AnalyticsHead } from "../../components/msd/AnalyticsHead";
import {
  AN_CARD,
  EmptyScope,
  EscalationCard,
  FeedbackCard,
  FlowCard,
  Highlights,
  NpsCard,
  SectionTitle,
  WaitingCard,
} from "../../components/msd/analytics";

// Workshop analytics, in the Maruti Service Desk design (docs/REPLICATION_BRIEF.md,
// phase 1). Figures come from the analytics endpoints; the per-car ones from
// /analytics/workshop/attention.
export default function WorkshopAnalytics() {
  const toast = useMsdToast();
  const nav = useNavigate();
  const scope = useAnalyticsScope();
  const { base, isSA, range } = scope;
  const [metric, setMetric] = useState("groups");
  const [granularity, setGranularity] = useState("day");

  const by = isSA ? "advisor" : "workshop";
  const jobs = useWorkshopVolume(base && { ...base, bucket: "day", by });
  const groupsCreated = useWorkshopGroupsCreated(base && { ...base, bucket: "day", by });
  const repairedByDay = useWorkshopEventVolume("repaired", base);
  const deliveredByDay = useWorkshopEventVolume("delivered", base);
  const tat = useWorkshopTat(base && { ...base, by });
  const cycle = useWorkshopCycleTime(base && { ...base, by });
  const attention = useWorkshopAttention(base);

  const cars = groupsCreated.data?.total ?? 0;
  const sinceLabel = format(range.from, "d MMM");
  const queries = [jobs, groupsCreated, repairedByDay, deliveredByDay, tat, cycle, attention];
  const failed = queries.some((q) => q.isError);
  const ready = queries.every((q) => !!q.data);

  const chart = { groups: groupsCreated, jobs, repaired: repairedByDay, delivered: deliveredByDay }[metric].data;
  const series = chart?.series || [];
  const average = useMemo(() => dailyAverage(chart?.total || 0, range.from, range.to), [chart, range]);

  if (!scope.allowed) {
    return (
      <div className={AN_CARD} data-testid="analytics-no-access">
        <p className="text-[15px] text-msd-muted">Workshop analytics isn&apos;t available for your role.</p>
      </div>
    );
  }

  const att = attention.data;
  const advisors = att?.awaiting_reply.advisors || [];
  const top = advisors[0];
  const reach = att ? att.nps.passives + att.nps.detractors : 0;
  const workshop = (name) => String(name || "").replace(/^Maruti /, "");
  // Opens the customer's group on the Advisor's desk.
  const openGroup = (p) => nav("/desk", { state: { openRow: p.group_row_id } });

  const highlights = att
    ? [
        {
          testid: "msd-hl-notjoined",
          color: "#F04438",
          icon: "userx",
          title: `${att.customers_not_joined} customers haven't joined their service group`,
          sub: "They won't see estimates, approvals or delivery updates. Resend the invite.",
          cta: "Resend invites",
          onClick: () => nav("/desk", { state: { bucket: "customer_not_joined" } }),
        },
        {
          testid: "msd-hl-reply",
          color: "#F79009",
          icon: "chat",
          title: top ? `${top.name} has ${top.unanswered} unanswered customer messages` : "No unanswered customer messages",
          sub: top ? `Across ${top.groups} service group${top.groups === 1 ? "" : "s"} at ${workshop(top.workshop)}.` : "",
          cta: "Open chats",
          onClick: () => nav("/desk", { state: { bucket: "unanswered" } }),
        },
        // Only when someone scored 8 or below: with no NPS answers, or none unhappy, there is nobody to follow up.
        ...(reach > 0
          ? [
              {
                testid: "msd-hl-nps",
                color: "#3A5F94",
                icon: "msg",
                title: `${reach} customer${reach === 1 ? " is" : "s are"} not happy to recommend us`,
                sub: "They scored 8 or below. Follow up before their next service visit.",
                cta: "See who",
                onClick: () => toast("Hover Needs follow-up in the Net Promoter Score card to see them"),
              },
            ]
          : []),
      ]
    : [];

  const locName = scope.loc === ALL ? "All locations" : scope.loc;
  const emptyText =
    scope.loc === ALL ? "No vehicles have gone through WhatsApp yet." : `No vehicles have gone through WhatsApp at ${scope.loc}.`;

  return (
    <MsdPage className="flex flex-col gap-6 [&>*]:shrink-0" data-testid="workshop-analytics">
        <AnalyticsHead
          scope={scope}
          title="Workshop analytics"
          description="How vehicles move through WhatsApp, from group created to delivered."
        />

        {failed ? (
          <div className={`${AN_CARD} text-center`}>
            <div className="text-[20px] font-semibold">Couldn&apos;t load the numbers</div>
          </div>
        ) : !ready ? null : cars === 0 ? (
          <EmptyScope text={emptyText} onSetup={() => toast("Journey setup lives in Settings")} />
        ) : (
          <>
            <FlowCard
              cars={cars}
              jobs={jobs.data.total}
              delivered={cycle.data.closed?.n || 0}
              repaired={tat.data.overall?.n || 0}
              repairHours={tat.data.overall?.mean_hours}
              cycleHours={cycle.data.closed?.mean_hours}
              sinceLabel={sinceLabel}
              metric={metric}
              onMetric={setMetric}
              series={series}
              average={average}
              granularity={granularity}
              onGranularity={setGranularity}
            />
            <Highlights items={highlights} />
            <SectionTitle style={{ marginBottom: -12 }}>What customers said</SectionTitle>
            <div className="grid grid-cols-2 gap-6 max-[1150px]:grid-cols-1">
              <NpsCard nps={att.nps} people={att.respondents || []} onOpen={openGroup} />
              <FeedbackCard items={att.feedback} people={att.respondents || []} onOpen={openGroup} />
            </div>
            <div className="grid grid-cols-2 gap-6 max-[1150px]:grid-cols-1">
              <WaitingCard
                advisors={advisors}
                managers={att.awaiting_reply.managers}
                onOpenAdvisor={(r) => nav("/desk", { state: { bucket: "unanswered", advisor: r.name } })}
              />
              <EscalationCard
                escalation={att.escalation}
                scopeLabel={locName}
                onOpen={(bucket) => nav("/desk", { state: { bucket } })}
              />
            </div>
          </>
        )}
    </MsdPage>
  );
}
